import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  getPatientById,
  getAppointmentById,
  getServiceById,
} from "../../api";
import PatientNotes from "./PatientNotes";
import DocumentsPanel from "../Documents/DocumentsPanel";
import { LinkNotes } from "./LinkNotes";
import PatientFileManager from "./PatientFiles";
import Diagnosis from "./Diagnosis";
import RecordModal from "../SyncTo/RecordModal";
import ClinicalHistoryFullPanel from "../ClinicalHistory/ClinicalHistoryFullPanel";
import { userCanSeeClinicalHistory, userCanGenerateDocuments } from "../ClinicalHistory/access";

/**
 * The practitioner half of the patient file: routes the page's rail sections
 * (Notes, AI History, Documents, Diagnoses, Link Notes, Patient Files) to
 * their panels, and owns the patient / appointment / service loads they
 * share.
 *
 * The section is driven from outside (patientsappointment.jsx owns the rail):
 * `controlledSection` sets it, `onSectionChange` reports any transition made
 * from in here (a recording finishing upload, AI History asking to open a
 * note) so the rail highlight stays in step.
 *
 * Notes used to be two sections here, "Appointment Notes" and "Previous
 * Notes", each mounting the same editor. They are one Notes section now; see
 * PatientNotes.jsx for why.
 */
export const SESSION_SECTIONS = ["notes", "history", "documents", "diagnosis", "linkNotes", "files"];

export default function Session({
  patient: patientProp,
  appointment: appointmentProp,
  controlledSection,
  onSectionChange,
  // A clinicalDocumentDrafts id to open on the Documents panel (a task's
  // "Open Document" lands here with it in router state).
  openDocumentId = null,
}) {
  const { id, appointmentId } = useParams();
  const hasAppointment = !!appointmentId && appointmentId !== "1";

  const [activeSection, setActiveSection] = useState(controlledSection || "notes");

  useEffect(() => {
    if (controlledSection && controlledSection !== activeSection) {
      setActiveSection(controlledSection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledSection]);

  useEffect(() => {
    onSectionChange?.(activeSection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  const [patient, setPatient] = useState(patientProp);
  const [appointment, setAppointment] = useState(appointmentProp);
  const [service, setService] = useState();
  const [isLoading, setIsLoading] = useState(true);

  // A note AI History asked to open. The token changes per request so the
  // same note can be asked for twice in a row.
  const [openNoteReq, setOpenNoteReq] = useState({ id: null, token: 0 });

  // Sync prop to state when the parent supplies these after first paint.
  // Convention: prop === undefined means "parent isn't supplying, fetch
  // yourself"; null means "parent is loading, wait"; populated means use it.
  useEffect(() => {
    if (patientProp) setPatient(patientProp);
  }, [patientProp]);

  useEffect(() => {
    if (appointmentProp) setAppointment(appointmentProp);
  }, [appointmentProp]);

  useEffect(() => {
    if (!appointmentId) return;
    let cancelled = false;
    async function loadPatientAndAppointment() {
      try {
        if (patientProp === undefined) {
          const patientData = await getPatientById(id);
          if (!cancelled) setPatient(patientData);
        }
        if (appointmentProp === undefined && hasAppointment) {
          const appointmentData = await getAppointmentById(appointmentId);
          if (!cancelled) setAppointment(appointmentData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    loadPatientAndAppointment();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, appointmentId]);

  // Service lookup runs whenever appointment.serviceId becomes available,
  // whether the appointment came from props or our own fetch.
  useEffect(() => {
    if (!appointment?.serviceId) return;
    let cancelled = false;
    getServiceById(appointment.serviceId)
      .then((s) => { if (!cancelled) setService(s); })
      .catch((err) => console.error("Error fetching service:", err));
    return () => { cancelled = true; };
  }, [appointment?.serviceId]);

  const formattedDateTime =
    appointment?.start && !isNaN(new Date(appointment.start))
      ? new Intl.DateTimeFormat("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          day: "2-digit",
          month: "short",
          year: "numeric",
        }).format(new Date(appointment.start))
      : "";

  // Human-readable label for a recording's IndexedDB row, so the orphan
  // prompt can say which patient and visit it belongs to.
  const patientFullName = [
    patient?.patientFullNames || patient?.patientFirstName || appointment?.patientFirstName,
    patient?.patientLastName || appointment?.patientLastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
  const patientLabel = patientFullName
    ? `${patientFullName}${formattedDateTime ? ` · ${formattedDateTime}` : ""}`
    : formattedDateTime;

  // The practice this patient file is being worked under: the appointment's,
  // else the first of the patient's practices the user belongs to. Never one
  // the user is not in: a shared patient's patientPractice[] lists every
  // practice, and the diagnoses and documents APIs refuse a practice the
  // caller and patient do not share.
  const practiceForPatient = useCallback(() => {
    if (appointment?.practiceId) return appointment.practiceId;
    let mine = [];
    try {
      const token = sessionStorage.getItem("user");
      const decoded = token ? jwtDecode(token) : null;
      mine = (Array.isArray(decoded?.practice) ? decoded.practice : []).map(String);
    } catch (_err) { /* no usable token: fall through to undefined */ }
    const list = Array.isArray(patient?.patientPractice) ? patient.patientPractice.map(String) : [];
    return list.find((p) => mine.includes(p)) || undefined;
  }, [appointment?.practiceId, patient?.patientPractice]);

  // A capture that finished uploading sits in Review Needed, at the top of
  // the Notes section. Take the user there so the recording they just made is
  // findable rather than invisible behind another section.
  useEffect(() => {
    const handler = (e) => {
      const detail = (e && e.detail) || {};
      if (!detail.patientId || String(detail.patientId) !== String(id)) return;
      setActiveSection("notes");
    };
    window.addEventListener("syncto:uploaded", handler);
    return () => window.removeEventListener("syncto:uploaded", handler);
  }, [id]);

  const openNoteFromHistory = useCallback((sessionId) => {
    if (!sessionId) return;
    setOpenNoteReq({ id: String(sessionId), token: Date.now() });
    setActiveSection("notes");
  }, []);

  // A request is consumed once the Notes section has acted on it, and
  // dropped outright when the patient changes: this component is reused
  // across patients, and a stale request would otherwise open one patient's
  // note inside another's file.
  const clearOpenNoteReq = useCallback(() => setOpenNoteReq({ id: null, token: 0 }), []);
  useEffect(() => {
    setOpenNoteReq({ id: null, token: 0 });
  }, [id]);

  if (isLoading && patientProp === undefined) return <p>Loading session data...</p>;

  const canSeeClinicalHistory = userCanSeeClinicalHistory();
  const canGenerateDocuments = userCanGenerateDocuments();

  const renderContent = () => {
    if (activeSection === "notes") {
      return (
        <PatientNotes
          patientId={id}
          appointmentId={hasAppointment ? appointmentId : null}
          appointment={hasAppointment ? appointment : null}
          service={hasAppointment ? service : null}
          patientLabel={patientLabel}
          practiceId={practiceForPatient()}
          openNoteId={openNoteReq.id}
          openNoteToken={openNoteReq.token}
          onOpenNoteHandled={clearOpenNoteReq}
          onAppointmentChanged={setAppointment}
        />
      );
    }

    if (activeSection === "linkNotes") {
      return (
        <LinkNotes
          patientId={id}
          patientFirstName={patient?.patientFirstName || appointment?.patientFirstName}
          patientLastName={patient?.patientLastName || appointment?.patientLastName}
          onSuccess={() => setActiveSection("notes")}
        />
      );
    }

    if (activeSection === "files") return <PatientFileManager patientId={id} />;
    if (activeSection === "documents" && canGenerateDocuments) {
      return (
        <DocumentsPanel
          patientId={id}
          practiceId={practiceForPatient()}
          appointmentId={hasAppointment ? appointmentId : null}
          patient={patient}
          openDraftId={openDocumentId}
        />
      );
    }
    if (activeSection === "diagnosis") {
      return (
        <Diagnosis
          patientId={id}
          practiceId={practiceForPatient()}
        />
      );
    }
    if (activeSection === "history" && canSeeClinicalHistory) {
      return (
        <ClinicalHistoryFullPanel
          active
          patientId={id}
          practiceId={practiceForPatient()}
          patientName={patientFullName || patient?.patientFullNames || ""}
          showHeader={false}
          onOpenNote={openNoteFromHistory}
        />
      );
    }

    return null;
  };

  return (
    <div className="ms-1">
      <div className="pt-1">
        {renderContent()}
      </div>

      {/* Combined RecordModal: owns mode picker, consent, and recording UI.
          Rendered exactly once per Session mount so it unmounts on in-app nav
          while the RecordingProvider state above the router keeps the
          recorder alive. Driven entirely by useRecording(). */}
      <RecordModal />
    </div>
  );
}
